import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MenubarImage from "../assets/menubar.png";
import { ReactComponent as UidoLoad1 } from "../assets/loader/uido_load_1.svg";
import { ReactComponent as UidoLoad2 } from "../assets/loader/uido_load_2.svg";
import { ReactComponent as UidoLoad3 } from "../assets/loader/uido_load_3.svg";
import { ReactComponent as UidoLoad4 } from "../assets/loader/uido_load_4.svg";
import { ReactComponent as UidoLoad5 } from "../assets/loader/uido_load_5.svg";
import { ReactComponent as Doswiadczenia } from "../assets/loader/doscwiadczenia.svg";
import { gsap } from "gsap";

const Loader = () => {
  const { t } = useTranslation();
  const loaderRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const animatedPhasesForLoad2Ref = useRef(new Set());
  const timelineRef = useRef(null);
  const didPhase2SwapRef = useRef(false);


  useEffect(() => {
    if (!isVisible) return; // skip loader when already seen

    const phaseDurationsSec = [
      0.8,  // 0
      1.0,  // 1
      0.5,  // 2
      .7,  // 3
      .5,  // 4
      .5,  // 5
      .5,  // 6
      .5,  // 7 - faster diamond morph
      .8,  // 8 - faster diamond morph to small
      0.4   // 9 - faster diamond shrink
    ];
    
    // Block all scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;
    document.body.classList.add('loader-active');
    const tl = gsap.timeline({ defaults: { ease: "none" } });
    timelineRef.current = tl;

    let acc = 0;
    phaseDurationsSec.forEach((d, i) => {
      tl.call(() => setPhaseIndex(i), [], acc);
      acc += d;
    });
    // Small delay after small diamond shows, then hide loader and trigger banner animations
    tl.call(() => { setPhaseIndex(9); }, [], acc);
    acc += 0.33;
    tl.call(() => {
      setIsVisible(false);
      // Restore scrolling
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = 'auto';
      document.body.classList.remove('loader-active');
      // Always scroll to top when loader finishes
      window.scrollTo({ top: 0, behavior: 'instant' });
      // Notify the app that loader finished (used for ERROR 909 overlay)
      try { window.dispatchEvent(new Event('loader:finished')); } catch {}
      // Do not auto-prime or auto-unmute audio on loader end
      // Restart banner animated headings (e.g., "Redesign Selektywny") after loader finishes
        // First trigger the overlay sweep
        document.querySelectorAll('.banner-animated-heading').forEach((el) => {
          el.classList.remove('show-text');
          el.classList.add('in-view');
        });
        document.querySelectorAll('.subheading.banner-animated-heading').forEach((el) => {
          el.classList.remove('show-text');
          el.classList.add('in-view');
        });
        // Then reveal text slightly after overlay sweep completes
          document.querySelectorAll('.banner-animated-heading').forEach((el) => el.classList.add('show-text'));
          document.querySelectorAll('.subheading.banner-animated-heading').forEach((el) => el.classList.add('show-text'));
    }, [], acc - 0.33);

    return () => {
      tl.kill();
      // Restore scrolling
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = 'auto';
      document.body.classList.remove('loader-active');
      // Ensure scroll to top on cleanup
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
  }, [isVisible]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("in-view");
        }
      },
      { threshold: 0.3 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Ensure uido_load_2 draws once linearly with correct path length in phases 1-3
  useEffect(() => {
    if (phaseIndex < 1 || phaseIndex > 3) return;
    const key = `p${phaseIndex}`;
    if (animatedPhasesForLoad2Ref.current.has(key)) return;
    const svg = loaderRef.current?.querySelector?.('.loader-load-2');
    if (!svg) return;
    const path = svg.querySelector('path');
    if (!path) return;

    try {
      const len = path.getTotalLength();
      path.style.transition = 'none';
      path.style.strokeDasharray = `${len}`;
      path.style.strokeDashoffset = `${len}`;
      path.style.animation = 'none';
      // reflow
      void path.getBoundingClientRect();
      // animate
      path.style.transition = 'stroke-dashoffset 1.2s linear';
      path.style.strokeDashoffset = '0';
      animatedPhasesForLoad2Ref.current.add(key);
    } catch {}
  }, [phaseIndex]);

  // Add a subtle settle pop for load-1 at the end of phase 2 swap
  useEffect(() => {
    if (phaseIndex !== 2) return;
    const root = loaderRef.current;
    if (!root) return;
    const el1 = root.querySelector('.loader-load-1');
    if (!el1) return;
    const timer = setTimeout(() => {
      try {
        el1.classList.add('settle-pop-down');
        setTimeout(() => el1.classList.remove('settle-pop-down'), 220);
      } catch {}
    }, 350);
    return () => clearTimeout(timer);
  }, [phaseIndex]);

  // Phase 1 -> 2: quick swap animation between load-2 and load-3 (CSS keyframes)
  useEffect(() => {
    if (phaseIndex !== 2 || didPhase2SwapRef.current) return;
    const root = loaderRef.current;
    if (!root) return;
    const el2 = root.querySelector('.loader-load-2');
    const el3 = root.querySelector('.loader-load-3');
    if (!el2 || !el3) return;
    try {
      el2.classList.add('play-swap-to-3');
      el3.classList.add('play-swap-to-2');
      didPhase2SwapRef.current = true;
      setTimeout(() => {
        el2.classList.remove('play-swap-to-3');
        el3.classList.remove('play-swap-to-2');
      }, 300);
      setTimeout(() => {
          el3.classList.add('settle-pop');
          setTimeout(() => el3.classList.remove('settle-pop'), 220);
      }, 350);
     
    } catch {}
  }, [phaseIndex]);

  // Phase 7-8: Smooth diamond morphing from extra-large to phase 8 state
  useEffect(() => {
    if (phaseIndex !== 7) return;
    
    const largeDiamond = document.querySelector('.loader-diamond-stage.large');
    if (!largeDiamond) return;

    // Create smooth GSAP animation for phase 7-8 (faster)
    const morphTimeline = gsap.timeline({
      ease: "power2.inOut",
      duration: 0.2 // Much faster GSAP animation
    });

    // Animate from extra-large (full screen) to phase 8 state
    morphTimeline.to(largeDiamond, {
      right: 'get-vw(202px)',
      top: 'get-vw(-837px)',
      width: 'get-vw(1516px)',
      height: 'get-vw(2602px)',
      scale: 1.0,
      rotation: 0,
      opacity: 1,
      duration: 0.2,
      ease: "power2.inOut"
    });

    return () => {
      morphTimeline.kill();
    };
  }, [phaseIndex]);

  // Phase 8-9: Smooth diamond morphing with GSAP (faster)
  useEffect(() => {
    if (phaseIndex !== 8) return;
    
    const largeDiamond = document.querySelector('.loader-diamond-stage.large');
    if (!largeDiamond) return;

    // Target values for small diamond position and size (responsive)
    const isMobile = window.innerWidth <= 768; // Mobile breakpoint
    
    const targetRight = 'auto';
    const targetLeft = isMobile ? '10.5208vw' : '10.5208333333vw';
    const targetTop = isMobile ? '23.4063vw' : '-42.59375vw';
    const targetWidth = '78.958333vw';
    const targetHeight = '135.5208333333vw';

    // Create smooth GSAP animation (much faster)
    const morphTimeline = gsap.timeline({
      ease: "power2.inOut",
      duration: 0.2 // Much faster GSAP animation
    });

    // Animate position and size smoothly
    morphTimeline.to(largeDiamond, {
      right: targetRight,
      left: targetLeft,
      top: targetTop,
      width: targetWidth,
      height: targetHeight,
      duration: 0.2,
      ease: "power2.inOut"
    });

    // Also animate transform for additional smoothness
    morphTimeline.to(largeDiamond, {
      scale: isMobile ? 0.2 : 0.1,
      rotation: 0,
      opacity: 0.8,
      duration: 0.2,
      ease: "power2.inOut"
    }, 0); // Start at the same time as position animation

    // TV closing effect - shrink to tiny point and disappear (faster)
    morphTimeline.to(largeDiamond, {
      scale: 0.01,
      opacity: 0,
      duration: 0.1, // Faster TV closing effect
      ease: "power2.in",
      delay: 0.05, // Shorter delay before TV closing effect
      force3D: true // Force hardware acceleration
    });

    return () => {
      morphTimeline.kill();
    };
  }, [phaseIndex]);

  if (!isVisible) return null;

  return (
    <div className={`loader-overlay phase-${phaseIndex}`}>
      {/* Phase 7: Big diamond-shaped background (for later morph) */}
      {phaseIndex === 7 && (
        <div className="loader-diamond-stage bg">
          <svg
            className="diamond-svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1516 928"
            aria-hidden
          >
            <path d="M757.907 -837L-1.04468e-05 464L757.907 1765L1516 464L757.907 -837Z" />
          </svg>
        </div>
      )}

      {/* Diamond morph layers for phases 8-9 */}
      {phaseIndex >= 8 && (
        <div className={`loader-diamond-stage ${phaseIndex === 9 ? 'small' : 'large'}`}>
          {phaseIndex === 9 ? (
            <svg
              className="diamond-svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 77 132"
              aria-hidden
            >
              <path d="M38.4953 -7.21824e-06L-1.02007e-05 66L38.4953 132L77 66L38.4953 -7.21824e-06Z" />
            </svg>
          ) : (
            <svg
              className="diamond-svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1516 928"
              aria-hidden
            >
              <path d="M757.907 -837L-1.04468e-05 464L757.907 1765L1516 464L757.907 -837Z" />
            </svg>
          )}
        </div>
      )}

      {/* Menubar image at the top (hide at phase >= 6) */}
      {phaseIndex >= 1 && phaseIndex < 6 && (
        <div className="loader-menubar">
          <img src={MenubarImage} alt="Menu" className="menubar-image" />
        </div>
      )}
      
      <div className="loader-container" ref={loaderRef}>
        <div className="loader-content">
          {/* Main text (start showing from phase 1) */}
          {phaseIndex >= 2 && (
            <div className={`loader-text ${phaseIndex >= 4 ? 'absolute' : ''}`}>
              <span className={`loader-text-load ${phaseIndex >= 4 ? 'is-ready' : ''} ${phaseIndex >= 5 ? 'hidden' : ''}`}>
                {phaseIndex >= 4 ? t("loader.ready") : t("loader.loading")}
              </span>
                <Doswiadczenia className={`loader-text-experience-svg ${phaseIndex >= 5 ? 'hidden' : ''}`} />
            </div>
          )}

          {/* Loader graphics sequence */}
          <div className="loader-graphics loader-three-d-icon">
            {/* Phase 0: only load-1. Phase >=6: hide load-1 */}
            {phaseIndex < 6 && <UidoLoad1 className={`loader-load-1 ${phaseIndex === 0 ? 'phase0' : ''}`} />}
            {phaseIndex >= 1 && phaseIndex < 7 && (
              <UidoLoad2 className={`loader-load-2 ${phaseIndex >= 6 ? 'recolor-orange' : ''}`} />
            )}
            {phaseIndex >= 1 && phaseIndex < 8 && (
              <UidoLoad3 className={`loader-load-3 ${phaseIndex === 6 ? 'recolor-blue-fill-orange-stroke' : ''} ${phaseIndex >= 7 ? 'recolor-blue-outline' : ''} ${phaseIndex === 4 ? 'nudged-up' : ''}`} />
            )}
            {/* Show 4-6 only in phases 1-2 */}
            {phaseIndex >= 1 && phaseIndex < 3 && <UidoLoad4 className="loader-load-4" />}
            {phaseIndex >= 1 && phaseIndex < 3 && <UidoLoad5 className="loader-load-5" />}

            {/* Orange light: single element that changes size classes across phases */}
            {phaseIndex >= 2 && phaseIndex <= 7 && (
              <div
                className={
                  `loader-light ` +
                  (phaseIndex === 2 ? 'size-sm ' : '') +
                  (phaseIndex === 3 ? 'size-md ' : '') +
                  (phaseIndex === 4 ? 'size-lg ' : '') +
                  (phaseIndex === 5 ? 'size-lg size-phase5 ' : '') +
                  (phaseIndex === 6 ? 'size-huge ' : '') +
                  (phaseIndex === 7 ? 'size-huge ' : '')
                }
              />
            )}
          </div>

          {/* Subtitle moved outside content to anchor to container height */}

          {/* Phases 7–8: cross-animate messages */}
          {(phaseIndex === 7 || phaseIndex === 8) && (
            <>
              <div className="loader-bottom-message">
                <span>{t("loader.message.bottom")}</span>
              </div>
              <div className="loader-center-message">
                <span>{t("loader.message.center")}</span>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Bottom subtitle anchored to overlay */}
      {phaseIndex >= 1 && phaseIndex < 6 && (
        <div className="loader-subtitle">
          <span className={`loader-subtitle-text ${(phaseIndex === 2 || phaseIndex === 4) ? 'orange' : ''}`}>
            {phaseIndex >= 5 ? t("loader.subtitle.forYou") : phaseIndex >= 3 ? t("loader.subtitle.meetingNeeds") : t("loader.subtitle")}
          </span>
        </div>
      )}
    </div>
  );
};

export default Loader; 